// Teacher → Tasks. Create a non-lesson task for a class (or a single student),
// see the board grouped by status, verify a done task or reopen it.
// docs/replan-v2.md R10.

import { useT } from '@fajrarabi/i18n/react';
import { Alert, Badge, Button, Card, CardBody, Field, Input, Skeleton } from '@fajrarabi/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import { ApiError } from '~/lib/api';
import {
  createTask,
  deleteTask,
  listCreatedTasks,
  transitionTask,
  type Task,
} from '~/lib/messaging';
import { listTeacherClasses } from '~/lib/school';

export const Route = createFileRoute('/$locale/teacher/tasks')({
  component: TeacherTasksScreen,
});

function TeacherTasksScreen() {
  const t = useT();
  const qc = useQueryClient();
  const [title, setTitle] = useState('');
  const [classId, setClassId] = useState('');
  const [dueAt, setDueAt] = useState('');

  const classes = useQuery({
    queryKey: ['teacher', 'classes'],
    queryFn: ({ signal }) => listTeacherClasses(signal),
  });
  const tasks = useQuery({
    queryKey: ['tasks', 'created'],
    queryFn: ({ signal }) => listCreatedTasks(signal),
  });

  const create = useMutation({
    mutationFn: () =>
      createTask({
        title: title.trim(),
        classId: classId || null,
        dueAt: dueAt ? new Date(dueAt).toISOString() : null,
      }),
    onSuccess: async () => {
      setTitle('');
      setDueAt('');
      await qc.invalidateQueries({ queryKey: ['tasks', 'created'] });
    },
  });

  const transition = useMutation({
    mutationFn: ({ id, status }: { id: string; status: Task['status'] }) =>
      transitionTask(id, status),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['tasks', 'created'] });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteTask(id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['tasks', 'created'] });
    },
  });

  const canCreate = title.trim() !== '' && classId !== '';

  return (
    <div className="mx-auto max-w-3xl px-6 py-10">
      <h1 className="text-2xl font-bold text-ink-900">{t('app.tasks.title')}</h1>

      <Card className="mt-6">
        <CardBody>
          {create.isError && (
            <Alert tone="danger" className="mb-4">
              {create.error instanceof ApiError
                ? create.error.message
                : t('app.common.error_generic')}
            </Alert>
          )}
          <form
            className="grid gap-4 sm:grid-cols-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (canCreate) create.mutate();
            }}
          >
            <div className="sm:col-span-2">
              <Field label={t('app.tasks.title_label')}>
                {(id) => (
                  <Input
                    id={id}
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder={t('app.tasks.title_placeholder')}
                    maxLength={240}
                  />
                )}
              </Field>
            </div>
            <Field label={t('app.tasks.pick_class')}>
              {(id) => (
                <select
                  id={id}
                  value={classId}
                  onChange={(e) => setClassId(e.target.value)}
                  className="h-11 w-full rounded-xl border border-ink-500/20 bg-white px-3 text-ink-900"
                >
                  <option value="">—</option>
                  {(classes.data ?? []).map((cls) => (
                    <option key={cls.id} value={cls.id}>
                      {cls.name}
                    </option>
                  ))}
                </select>
              )}
            </Field>
            <Field label={t('app.tasks.due_date')}>
              {(id) => (
                <Input
                  id={id}
                  type="date"
                  value={dueAt}
                  onChange={(e) => setDueAt(e.target.value)}
                />
              )}
            </Field>
            <div className="sm:col-span-2">
              <Button type="submit" disabled={!canCreate || create.isPending}>
                {t('app.tasks.create')}
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>

      <div className="mt-8 space-y-3">
        {tasks.isLoading ? (
          <Skeleton className="h-20 w-full" />
        ) : (tasks.data?.length ?? 0) === 0 ? (
          <p className="text-sm text-ink-700">{t('app.tasks.empty_teacher')}</p>
        ) : (
          tasks.data!.map((task) => (
            <TaskRow
              key={task.id}
              task={task}
              onVerify={() => transition.mutate({ id: task.id, status: 'verified' })}
              onReopen={() => transition.mutate({ id: task.id, status: 'open' })}
              onRemove={() => remove.mutate(task.id)}
            />
          ))
        )}
      </div>
    </div>
  );
}

const STATUS_TONE: Record<Task['status'], 'neutral' | 'warning' | 'success' | 'brand'> = {
  open: 'neutral',
  in_progress: 'warning',
  done: 'brand',
  verified: 'success',
};

function TaskRow({
  task,
  onVerify,
  onReopen,
  onRemove,
}: {
  task: Task;
  onVerify: () => void;
  onReopen: () => void;
  onRemove: () => void;
}) {
  const t = useT();
  return (
    <div className="flex items-center justify-between gap-4 rounded-2xl bg-white p-4 shadow-[0_12px_30px_-22px_rgba(31,26,23,0.25)] ring-1 ring-burgundy-500/10">
      <div className="min-w-0">
        <p className="truncate font-semibold text-ink-900">{task.title}</p>
        <p className="mt-0.5 text-sm text-ink-500">
          {task.assigneeName}
          {task.dueAt ? ` · ${task.dueAt.slice(0, 10)}` : ''}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Badge tone={STATUS_TONE[task.status]}>{t(`app.tasks.status_${task.status}`)}</Badge>
        {task.status === 'done' && (
          <Button size="sm" onClick={onVerify}>
            {t('app.tasks.verify')}
          </Button>
        )}
        {task.status === 'verified' && (
          <Button variant="ghost" size="sm" onClick={onReopen}>
            {t('app.tasks.reopen')}
          </Button>
        )}
        <Button variant="ghost" size="sm" onClick={onRemove}>
          {t('app.tasks.remove')}
        </Button>
      </div>
    </div>
  );
}
